import type { FunctionComponent } from 'react';
import React, { useMemo, useCallback, useRef, useState } from 'react';
import type {
  SwitcherView,
  SwitcherView_ViewType,
  ViewOption,
} from './ViewSwitcher';
import { getViewUrl } from './getViewUrl';
import cx from 'classnames';
import { forTemplate } from '@trello/i18n';
// eslint-disable-next-line @trello/less-matches-component
import styles from './ViewSwitcher.less';
const format = forTemplate('view_switcher');

import { RouterLink } from 'app/src/components/RouterLink';
import { DynamicButton } from '@trello/dynamic-tokens';
import { DownIcon } from '@trello/nachos/icons/down';
import { Popover, PopoverScreen, usePopover } from '@trello/nachos/popover';
import { BoardHeaderTestIds } from '@trello/test-ids';
import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';
import { LazyArrangeViewButtonsPopoverScreen } from './LazyArrangeViewButtonsPopoverScreen';
import { mergeRefs } from 'app/src/mergeRefs';
import type { TestId } from '@trello/test-ids';

interface ViewSwitcherButtonProps {
  idBoard: string;
  backgroundBrightness: string;
  currentView: SwitcherView_ViewType | null;
  viewOptions: { [key in SwitcherView_ViewType]: ViewOption };
  switcherViews: SwitcherView[];
  hasViewsFeature: boolean;
  memberCanEditBoard: boolean;
  analyticsContainers?: Parameters<
    typeof Analytics.sendClickedButtonEvent
  >[0]['containers'];
}

const ViewSwitcherButtonsTestIdsMap: {
  [key in SwitcherView_ViewType]: TestId;
} = {
  Table: BoardHeaderTestIds.ViewSwitcherButtonTable,
  Board: BoardHeaderTestIds.ViewSwitcherButtonBoard,
  Calendar: BoardHeaderTestIds.ViewSwitcherButtonCalendar,
  Dashboard: BoardHeaderTestIds.ViewSwitcherButtonDashboard,
  Timeline: BoardHeaderTestIds.ViewSwitcherButtonTimeline,
  Map: BoardHeaderTestIds.ViewSwitcherButtonMap,
};

export const ViewSwitcherButtons: FunctionComponent<ViewSwitcherButtonProps> = ({
  idBoard,
  backgroundBrightness,
  currentView,
  viewOptions,
  switcherViews,
  hasViewsFeature,
  memberCanEditBoard,
  analyticsContainers = {},
}) => {
  const onViewClick = useCallback(
    (currentlyOnView: boolean, view: ViewOption) => {
      return () => {
        if (!currentlyOnView) {
          Analytics.sendClickedButtonEvent({
            buttonName: 'boardViewSwitcherButton',
            source: 'boardScreen',
            containers: analyticsContainers,
            attributes: {
              viewClicked: view.name,
            },
          });
        }
      };
    },
    [analyticsContainers],
  );

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<Element>, callback: () => void) => {
      if (e.key === 'Enter') {
        callback();
      }
    },
    [],
  );

  const onShow = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardViewSwitcherMoreButton',
      source: 'boardScreen',
      containers: analyticsContainers,
    });
  }, [analyticsContainers]);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  // When the popover is opened, this state is set to the x offset of the caret
  // button. The popover is anchored to a dummy div positioned with that offset
  // so that it doesn't move when enabling or disabling views.
  const [persistentPopoverOffset, setPersistentPopoverOffset] = useState<
    number | undefined
  >(undefined);
  const popoverAnchorOffsetX = useMemo(() => {
    return persistentPopoverOffset ?? buttonRef.current?.offsetLeft ?? 0;
  }, [persistentPopoverOffset]);

  const {
    triggerRef,
    targetRef,
    hide,
    toggle,
    popoverProps,
  } = usePopover<HTMLButtonElement>({
    onShow,
  });

  // Because the popover is anchored to a dummy div instead of the button, this
  // ensures that closing out of the popover returns focus to the button.
  const onPopoverHide = useCallback(() => {
    hide();
  }, [hide]);

  const onToggle = useCallback(() => {
    setPersistentPopoverOffset(buttonRef.current?.offsetLeft);
    toggle();
  }, [toggle]);

  // The buttonRef is used to persist the location the popover was opened from,
  // the triggerRef is supplied from the popover to make sure clicking the
  // button isn't registered as an outsideClick.
  const combinedButtonRefs = mergeRefs(buttonRef, triggerRef);

  return (
    <div className={styles.switcherButtonsContainer}>
      {switcherViews.map(({ viewType, enabled }) => {
        if (!enabled) {
          return;
        }
        const view = viewOptions[viewType];
        const currentlyOnView = viewType === currentView;
        const iconBrightness =
          backgroundBrightness === 'light' ? 'light' : 'dark';
        const inverseBrightness =
          backgroundBrightness === 'light' ? 'dark' : 'light';
        const buttonActiveClass =
          backgroundBrightness === 'light'
            ? styles.viewSwitcherButtonActiveLightBg
            : styles.viewSwitcherButtonActiveDarkBg;
        const testId = ViewSwitcherButtonsTestIdsMap[viewType];

        return (
          <RouterLink
            href={getViewUrl(idBoard, view.name)}
            title={format(viewType)}
            key={viewType}
            tabIndex={0}
            role={'link'}
            onClick={onViewClick(currentlyOnView, view)}
            aria-label={format(viewType)}
            testId={testId}
            // eslint-disable-next-line react/jsx-no-bind
            onKeyUp={(e) => onKeyUp(e, onViewClick(currentlyOnView, view))}
            className={cx(
              styles.viewSwitcherButton,
              currentlyOnView && buttonActiveClass,
            )}
          >
            <>
              <span
                className={styles.viewSwitcherIcon}
                data-testid={BoardHeaderTestIds.ViewSwitcherButtonIcon}
              >
                {React.createElement(view.icon, {
                  size: 'small',
                  color: currentlyOnView ? iconBrightness : inverseBrightness,
                })}
              </span>
              <span data-testid={BoardHeaderTestIds.ViewSwitcherButtonText}>
                {format(viewType)}
              </span>
            </>
          </RouterLink>
        );
      })}
      <DynamicButton
        className={styles.overflowIcon}
        aria-label={format('customize-views')}
        iconBefore={<DownIcon />}
        ref={combinedButtonRefs}
        onClick={onToggle}
        data-testid={BoardHeaderTestIds.ViewSwitcherButtonMore}
      />
      <div
        ref={targetRef}
        className={styles.dummyPopoverTarget}
        style={{
          left: popoverAnchorOffsetX,
        }}
      ></div>
      <Popover
        {...popoverProps}
        onHide={onPopoverHide}
        title={
          hasViewsFeature
            ? memberCanEditBoard
              ? format('add-and-edit-views')
              : format('views')
            : dontUpsell()
            ? format('views')
            : format('upgrade-for-views')
        }
      >
        <PopoverScreen id={0}>
          <LazyArrangeViewButtonsPopoverScreen
            idBoard={idBoard}
            viewOptions={viewOptions}
            switcherViews={switcherViews}
            hasViewsFeature={hasViewsFeature}
            memberCanEditBoard={memberCanEditBoard}
            analyticsContainers={analyticsContainers}
          />
        </PopoverScreen>
      </Popover>
    </div>
  );
};
